import type React from 'react';

export interface TabItem {
  key: string; // уникальный ключ (обычно pathname)
  url: string; // полный URL с search
  title: string; // заголовок вкладки
  lastOpenedAt: number; // unix epoch (ms), когда вкладка была открыта/активирована
  badge?: React.ReactNode; // узел или компонент-бейдж
}

export interface TabControllerState {
  tabs: TabItem[];
  activeUrl: string | null;
  activeKey: string | null;
  isFirstActive: boolean;
  activeTabTouchesRight: boolean;
}

export class TabController {
  private static instance: TabController;
  private listeners = new Set<() => void>();
  private tabs = new Map<string, TabItem>();
  // Позиции гридов живут только до закрытия вкладки / перезагрузки приложения.
  private tablePositions = new Map<string, Map<string, string>>();
  private activeUrl: string | null = null;
  private activeKey: string | null = null;
  private isFirstActive: boolean = false;
  // История активированных вкладок (URL), без дублей, последний — самый свежий
  private activeHistory: string[] = [];
  private activeTabTouchesRight: boolean = false;
  private state: TabControllerState = {
    tabs: [],
    activeUrl: null,
    activeKey: null,
    isFirstActive: false,
    activeTabTouchesRight: false,
  };

  private constructor() {
    this.restore();
  }

  static getInstance(): TabController {
    if (!TabController.instance) TabController.instance = new TabController();
    return TabController.instance;
  }

  // Подписка/снапшот
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): TabControllerState => this.state;

  private emit(): void {
    for (const l of this.listeners) l();
  }

  private updateState(): void {
    const next: TabControllerState = {
      tabs: Array.from(this.tabs.values()),
      activeUrl: this.activeUrl,
      activeKey: this.activeKey,
      isFirstActive: this.isFirstActive,
      activeTabTouchesRight: this.activeTabTouchesRight,
    };
    try {
      const prevStr = JSON.stringify(this.state);
      const nextStr = JSON.stringify(next);
      if (prevStr === nextStr) return;
    } catch {
      /* noop */
    }
    this.state = next;
    this.emit();
  }

  // API
  addTab(item: TabItem): void {
    if (!this.tabs.has(item.key)) {
      this.tabs.set(item.key, {
        ...item,
        lastOpenedAt: item.lastOpenedAt ?? Date.now(),
      });
      this.persist();
      this.updateState();
    }
  }

  /** Обновляет timestamp последнего открытия вкладки. */
  private touchTab(key: string, openedAt: number): boolean {
    const tab = this.tabs.get(key);
    if (!tab) return false;
    this.tabs.set(key, {
      ...tab,
      lastOpenedAt: openedAt,
    });
    return true;
  }

  removeTab(key: string): void {
    this.tablePositions.delete(key);
    const removed = this.tabs.get(key);
    if (this.tabs.delete(key)) {
      this.persist();
      // Удаляем все вхождения удаляемого URL из истории
      if (removed?.url) {
        this.activeHistory = this.activeHistory.filter(u => u !== removed.url);
      }
      // Если удаляем активную вкладку — пытаемся активировать последнюю из истории, ещё существующую
      if (removed?.url && this.activeUrl === removed.url) {
        const existingUrls = new Set(Array.from(this.tabs.values()).map(t => t.url));
        const candidate = [...this.activeHistory].reverse().find(u => existingUrls.has(u)) || null;
        this.activeUrl = candidate;
      }
      this.updateState();
    }
  }

  removeAllTabs(): void {
    this.tablePositions.clear();
    if (this.tabs.size === 0) return;
    this.tabs.clear();
    this.activeHistory = [];
    this.activeUrl = null;
    this.persist();
    this.updateState();
  }

  setTitle(key: string, title: string): void {
    const t = this.tabs.get(key);
    if (t && t.title !== title) {
      this.tabs.set(key, { ...t, title });
      this.persist();
      this.updateState();
    }
  }

  getTableCenteredRowId(tabKey: string, tableId: string): string | null {
    return this.tablePositions.get(tabKey)?.get(tableId) ?? null;
  }

  /** Не эмитим и не пишем localStorage на прокрутке. Закрытая вкладка не получает новое состояние. */
  setTableCenteredRowId(tabKey: string, tableId: string, rowId: string | null): void {
    if (!this.tabs.has(tabKey)) return;
    if (rowId === null) {
      this.tablePositions.get(tabKey)?.delete(tableId);
      return;
    }
    let positions = this.tablePositions.get(tabKey);
    if (!positions) {
      positions = new Map();
      this.tablePositions.set(tabKey, positions);
    }
    positions.set(tableId, rowId);
  }

  /**
   * Обновляет URL существующей вкладки без изменения ключа
   */
  setUrl(key: string, url: string): void {
    const t = this.tabs.get(key);
    if (t && t.url !== url) {
      this.tabs.set(key, { ...t, url });
      this.persist();
      this.updateState();
    }
  }

  /**
   * Обновляет URL вкладки и активность за один проход,
   * чтобы избежать двойных перерисовок UI.
   */
  setUrlAndActivate(key: string, url: string): void {
    let changed = false;
    const t = this.tabs.get(key);
    const now = Date.now();

    if (t && t.url !== url) {
      this.tabs.set(key, { ...t, url });
      changed = true;
    }

    if (this.activeUrl !== url) {
      this.activeUrl = url;
      if (url) {
        this.activeHistory = this.activeHistory.filter(u => u !== url);
        this.activeHistory.push(url);
        if (this.activeHistory.length > 100) this.activeHistory.shift();
      }
      changed = true;
    }

    if (this.activeKey !== key) {
      this.activeKey = key;
      changed = true;
    }

    if (this.touchTab(key, now)) {
      changed = true;
    }

    if (changed) {
      this.persist();
      this.updateState();
    }
  }

  setBadge(key: string, badge: React.ReactNode): void {
    const t = this.tabs.get(key);
    if (t && t.badge !== badge) {
      this.tabs.set(key, { ...t, badge });
      this.updateState();
    }
  }

  activate(url: string | null): void {
    if (this.activeUrl === url) {
      if (url) {
        const key = url.split('?')[0];
        if (this.touchTab(key, Date.now())) {
          this.persist();
          this.updateState();
        }
      }
      return;
    }
    this.activeUrl = url;
    this.activeKey = url ? url.split('?')[0] : null;
    // Обновляем стек истории: убираем текущее вхождение и добавляем в конец
    if (url) {
      this.activeHistory = this.activeHistory.filter(u => u !== url);
      this.activeHistory.push(url);
      // Ограничим размер истории (например, 100 последних)
      if (this.activeHistory.length > 100) this.activeHistory.shift();

      const key = url.split('?')[0];
      if (this.touchTab(key, Date.now())) {
        this.persist();
      }
    }
    this.updateState();
  }

  hasTab(key: string): boolean {
    return this.tabs.has(key);
  }

  getTab(key: string): TabItem | undefined {
    return this.tabs.get(key);
  }

  /**
   * Перемещает вкладку в конец порядка отображения.
   * Используется при выборе вкладки из hidden-меню.
   */
  moveToEnd(key: string): void {
    const tab = this.tabs.get(key);
    if (!tab) return;

    const keys = Array.from(this.tabs.keys());
    if (keys.length > 0 && keys[keys.length - 1] === key) {
      return;
    }

    this.tabs.delete(key);
    this.tabs.set(key, tab);
    this.persist();
    this.updateState();
  }

  setActiveTabTouchesRight(touches: boolean): void {
    if (this.activeTabTouchesRight !== touches) {
      this.activeTabTouchesRight = touches;
      this.updateState();
    }
  }

  setIsFirstActive(isFirst: boolean): void {
    if (this.isFirstActive !== isFirst) {
      this.isFirstActive = isFirst;
      this.updateState();
    }
  }

  // Persistence
  private persist(): void {
    try {
      const simple = Array.from(this.tabs.values()).map(t => ({
        key: t.key,
        url: t.url,
        title: t.title,
        lastOpenedAt: t.lastOpenedAt,
      }));
      localStorage.setItem('aya-tabs', JSON.stringify(simple));
    } catch {
      /* noop */
    }
  }

  private restore(): void {
    try {
      const raw = localStorage.getItem('aya-tabs');
      if (!raw) return;
      const parsed = JSON.parse(raw) as Array<{
        key: string;
        url: string;
        title: string;
        lastOpenedAt?: number;
      }>;
      for (const t of parsed) {
        this.tabs.set(t.key, {
          ...t,
          lastOpenedAt: t.lastOpenedAt ?? 0,
        });
      }
      this.updateState();
    } catch {
      /* noop */
    }
  }
}
